import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../../primitives/Input';
import { Icon } from '../../primitives/Icon';
import { MonkeyProps } from '../../types/monkey.types';
import { PlaceAutocomplete, PlaceAutocompleteResult } from '../../../../components/shared/PlaceAutocomplete';
import { useGoogleMaps } from '../../../../contexts/GoogleMapsContext';
import './LocationFilter.css';
import { useLocationStore } from '../../../../stores/locationStore';
import { useMapStore } from '../../../../stores/mapStore';

interface LocationFilterProps extends MonkeyProps {
  onLocationChange?: (location: {
    latitude: number;
    longitude: number;
    shouldCenter?: boolean;
  }) => void;
  onPlaceSelected?: () => void;
  isLoading?: boolean;
  currentLocation?: { latitude: number; longitude: number } | null;
}

const LocationFilter: React.FC<LocationFilterProps> = ({
  onLocationChange,
  onPlaceSelected,
  isLoading,
  currentLocation,
  showMonkey = false,
  monkeyVariant = 'singeOrange',
  monkeyType = 'walking',
  monkeyLookAt = 'right',
  monkeyPosition = 'position1'
}) => {
  const setLocation = useLocationStore(state => state.setLocation);
  const flyTo = useMapStore(state => state.flyTo);
  const [searchInput, setSearchInput] = useState('');
  const [isGeolocating, setIsGeolocating] = useState(false);
  const selectedNameRef = useRef<string>('');

  // Utiliser le contexte Google Maps global - lazy load
  const { isLoaded, loadError, requestLoad } = useGoogleMaps();

  // Trigger immédiat si le composant monte (ex: FilterDrawer ouvert)
  useEffect(() => {
    requestLoad();
  }, [requestLoad]);

  // Gérer la position actuelle seulement si pas de saisie manuelle récente
  useEffect(() => {
    if (!window.google || selectedNameRef.current) return;
    
    if (currentLocation) {
      // Vérifier que l'API Google Maps est chargée
      if (window.google?.maps?.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({
          location: { lat: currentLocation.latitude, lng: currentLocation.longitude }
        }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            const address = results[0].formatted_address;
            // Seulement mettre à jour si l'utilisateur n'a pas de saisie en cours
            if (!searchInput || searchInput.includes('📍')) {
              setSearchInput(address);
            }
          }
        });
      } else {
        // Si l'API n'est pas chargée, afficher les coordonnées seulement si pas de saisie
        if (!searchInput || searchInput.includes('📍')) {
          setSearchInput(`${currentLocation.latitude.toFixed(4)}, ${currentLocation.longitude.toFixed(4)}`);
        }
      }
    } else if (!searchInput.includes('📍')) {
      // Seulement effacer si ce n'est pas une géolocalisation récente
      setSearchInput('');
    }
  }, [currentLocation]);
  
  const handlePlaceSelect = async (result: PlaceAutocompleteResult) => {
    if (result.location) {
      const lat = result.location.lat;
      const lng = result.location.lng;

      // 1. Déclencher le flyTo via mapStore (centrage de la carte)
      flyTo(lat, lng);

      // 2. Mettre à jour la position dans locationStore
      await setLocation(lat, lng, false, 'search'); // shouldCenter=false car flyTo le fait déjà

      // Callback optionnel pour compatibilité
      if (onLocationChange) {
        onLocationChange({
          latitude: lat,
          longitude: lng,
          shouldCenter: false // Plus besoin, flyTo le fait
        });
      }

      // Utiliser directement le nom de l'établissement s'il existe
      const displayName = result.displayName && result.types?.includes('establishment')
        ? result.displayName
        : result.formattedAddress || '';

      selectedNameRef.current = displayName;
      setSearchInput(displayName);

      // Fermer le drawer après sélection
      if (onPlaceSelected) {
        onPlaceSelected();
      }
    }
  };

  // Vérifier périodiquement si la valeur a été écrasée
  useEffect(() => {
    if (!selectedNameRef.current) return;

    const checkInterval = setInterval(() => {
      const inputElement = document.getElementById('location-input') as HTMLInputElement;
      if (inputElement && inputElement.value !== selectedNameRef.current) {
        setSearchInput(selectedNameRef.current);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      selectedNameRef.current = '';
    }, 1000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [searchInput, currentLocation]);

  const handleClear = () => {
    setSearchInput('');
    selectedNameRef.current = '';
    // Remettre le focus sur l'input après avoir effacé
    const inputElement = document.getElementById('location-input') as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
    }
  };

  const handleGeolocation = () => {

    
          // Vérifications de base
      if (!navigator.geolocation) {
        alert('❌ Geolocation not supported on this device');
        return;
      }

      if (!window.isSecureContext) {
        alert('❌ Geolocation requires a secure connection (HTTPS)');
        return;
      }

    setIsGeolocating(true);
    
    const timeoutId = setTimeout(() => {
      setIsGeolocating(false);
              alert('⏰ Geolocation is taking too long. Check your permissions and try again.');
    }, 15000);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        clearTimeout(timeoutId);
        
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        try {
          // Vérifier la validité
          if (isNaN(lat) || isNaN(lng)) {
            throw new Error('Invalid coordinates');
          }

          // 1. Déclencher le flyTo via mapStore (centrage de la carte)
          flyTo(lat, lng);

          // 2. Mettre à jour la position dans locationStore
          await setLocation(lat, lng, false, 'geolocation'); // shouldCenter=false car flyTo le fait déjà

          // Callback optionnel pour compatibilité
          if (onLocationChange) {
            onLocationChange({
              latitude: lat,
              longitude: lng,
              shouldCenter: false // Plus besoin, flyTo le fait
            });
          }
          
          // Afficher les coordonnées
          const displayCoords = `📍 ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          setSearchInput(displayCoords);

          // Fermer le drawer après géolocalisation (même comportement que la sélection d'adresse)
          if (onPlaceSelected) {
            onPlaceSelected();
          }

        } catch (error) {
          console.error('❌ Erreur traitement position:', error);
          alert(`❌ Erreur: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
          setIsGeolocating(false);
        }
      },
      (error) => {
        clearTimeout(timeoutId);

        setIsGeolocating(false);

        // Gérer les différents types d'erreurs
        switch (error.code) {
          case 1: // PERMISSION_DENIED
            alert('🔒 Location access blocked\n\nTo use your location:\n• Click the lock icon (🔒) in your browser\'s address bar\n• Allow location access for this site\n• Click the location button again\n\nOr type your city in the search box above.');
            break;
          case 2: // POSITION_UNAVAILABLE
            alert('📡 Location unavailable\n\nYour device cannot determine your location right now. Please type your city in the search box above.');
            break;
          case 3: // TIMEOUT
            alert('⏰ Location timeout\n\nIt took too long to get your location. Please try again or type your city in the search box above.');
            break;
          default:
            console.log('🌍 Géolocalisation refusée ou échouée:', error.message);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  return (
    <div className="location-filter">
      <div className="location-input-wrapper">
        <div className="location-input-container">
          {isLoaded ? (
            <PlaceAutocomplete
              placeholder="Where?"
              value={searchInput}
              onChange={(value) => setSearchInput(value)}
              onPlaceSelect={handlePlaceSelect}
              icon="MapPin"
              disabled={isLoading}
              className={`location-input ${searchInput ? 'has-clear' : ''}`}
            />
          ) : (
            <Input
              id="location-input"
              name="location"
              type="text"
              value={searchInput}
              placeholder={loadError ? "Search unavailable - use geolocation" : "Loading..."}
              icon="MapPin"
              className="location-input"
              disabled
              onChange={() => {}}
              showMonkey={showMonkey}
              monkeyVariant={monkeyVariant}
              monkeyType={monkeyType}
              monkeyLookAt={monkeyLookAt}
              monkeyPosition={monkeyPosition}
            />
          )}
          {searchInput && isLoaded && (
            <button
              type="button"
              className="location-clear-btn"
              onClick={handleClear}
              aria-label="Effacer l'adresse"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
        {/* Bouton géolocalisation toujours visible - ne dépend pas de Google Maps */}
        <button
          className={`location-geolocate-btn ${isGeolocating ? 'loading' : ''}`}
          onClick={handleGeolocation}
          disabled={isLoading || isGeolocating}
          aria-label="Use my location"
        >
          <Icon name={isGeolocating ? "Loader" : "Crosshair"} size={20} />
        </button>
      </div>
    </div>
  );
};

export default LocationFilter;